import React, { useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { userService, cookieService } from '../../_services';
import { STRING_CONSTANTS } from '../../utils/constants/stringConstants';
import ValidationErrorToast from '../../components/validationErrorToast';

interface AdminLoginProps extends RouteComponentProps {}

const AdminLogin: React.FC<AdminLoginProps> = (props) => {
    const [isValidating, setIsValidating] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        validateTokenAndLogin();
    }, []);

    const validateTokenAndLogin = async () => {
        // Get token from URL query params
        const urlParams = new URLSearchParams(window.location.search);
        const token = urlParams.get('token');

        if (!token) {
            setError('No login token provided');
            setIsValidating(false);
            return;
        }

        try {
            console.log('[AdminLogin] Validating token:', token);
            
            // Validate the token with the backend - this now returns everything we need!
            const response = await userService.aiRequest({
                method: 'validate-admin-login-token',
                data: { token }
            }, false); // Don't require auth for this call

            console.log('[AdminLogin] Token validation response:', response);

            if (response.data?.success && response.data?.user) {
                const userData = response.data.user;
                
                console.log('[AdminLogin] User data received:', userData);
                
                // Check if we have the token structure we need
                if (userData.user && userData.user.token) {
                    // Store the auth tokens
                    localStorage.setItem('access_token', userData.user.token.access);
                    localStorage.setItem('refresh_token', userData.user.token.refresh);
                    localStorage.setItem('user_data', JSON.stringify(userData.user));
                    localStorage.setItem('isGuest', 'false');
                    localStorage.setItem('isAdminLogin', 'true'); // Mark this as an admin login
                    
                    cookieService.saveCookie(STRING_CONSTANTS.cookieStrings.token, userData.user.token.access);
                    cookieService.saveCookie(STRING_CONSTANTS.cookieStrings.user, userData.user);
                    cookieService.saveCookie(STRING_CONSTANTS.cookieStrings.isGuest, false);

                    console.log('[AdminLogin] Tokens stored, redirecting to /ar');

                    // Small delay to ensure everything is saved
                    setTimeout(() => {
                        props.history.push('/ar');
                    }, 500);
                } else {
                    console.error('[AdminLogin] Invalid user data structure:', userData);
                    throw new Error('Invalid authentication data received');
                }
            } else {
                const errorMsg = response.data?.error || 'Invalid or expired token';
                console.error('[AdminLogin] Validation failed:', errorMsg);
                setError(errorMsg);
                ValidationErrorToast('Error', errorMsg);
            }
        } catch (error: any) {
            console.error('[AdminLogin] Error:', error);
            const errorMsg = error.response?.data?.error || error.message || 'Failed to validate login token';
            setError(errorMsg);
            ValidationErrorToast('Error', errorMsg);
        } finally {
            setIsValidating(false);
        }
    };

    const styles: { [key: string]: React.CSSProperties } = {
        container: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            backgroundColor: '#F8F9FA',
            fontFamily: "'DM Sans', sans-serif",
        },
        card: {
            background: '#FFFFFF',
            borderRadius: '12px',
            padding: '40px',
            maxWidth: '500px',
            width: '100%',
            textAlign: 'center',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        },
        title: {
            fontSize: '24px',
            fontWeight: '700',
            color: '#141414',
            marginBottom: '20px',
        },
        message: {
            fontSize: '16px',
            color: '#555555',
            marginBottom: '20px',
        },
        error: {
            fontSize: '16px',
            color: '#D92D20',
            marginBottom: '20px',
        },
        spinner: {
            width: '3rem',
            height: '3rem',
            color: '#0065FF',
            margin: '0 auto',
        },
    };

    return (
        <div style={styles.container}>
            <div style={styles.card}>
                {isValidating ? (
                    <>
                        <h1 style={styles.title}>Logging you in...</h1>
                        <p style={styles.message}>Please wait while we authenticate your session</p>
                        <div className="spinner-border" style={styles.spinner} role="status">
                            <span className="sr-only">Loading...</span>
                        </div>
                    </>
                ) : error ? (
                    <>
                        <h1 style={styles.title}>Login Failed</h1>
                        <p style={styles.error}>{error}</p>
                        <p style={styles.message}>
                            This login link may have expired or already been used. 
                            Please request a new login link from the admin panel.
                        </p>
                    </>
                ) : (
                    <>
                        <h1 style={styles.title}>Success!</h1>
                        <p style={styles.message}>Redirecting to dashboard...</p>
                    </>
                )}
            </div>
        </div>
    );
};

export default AdminLogin;